import jsPDF from 'jspdf';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useRef, useState } from 'react';

interface PropertyItem {
  description: string;
  propertyNumber: string;
  unitOfMeasure: string;
  unitValue: number;
  totalAmount: number;
  icsNo: string;
  dateAcquired: string;
  remarks: string;
}

const initialItem: PropertyItem = {
  description: '',
  propertyNumber: '',
  unitOfMeasure: '',
  unitValue: 0,
  totalAmount: 0,
  icsNo: '',
  dateAcquired: '',
  remarks: '',
};

const PropertyInventoryForm: React.FC = () => {
  const [item, setItem] = useState<PropertyItem>(initialItem);
  const [logoImage, setLogoImage] = useState<string | null>(null);
  const [generatedQR, setGeneratedQR] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fields: {
    name: keyof PropertyItem;
    label: string;
    type?: string;
    required: boolean;
  }[] = [
    { name: 'description', label: 'Description', required: true },
    { name: 'propertyNumber', label: 'Property Number', required: true },
    { name: 'unitOfMeasure', label: 'Unit of Measure', required: true },
    { name: 'unitValue', label: 'Unit Value', type: 'number', required: true },
    {
      name: 'totalAmount',
      label: 'Total Amount',
      type: 'number',
      required: true,
    },
    { name: 'icsNo', label: 'ICS No.', required: true },
    {
      name: 'dateAcquired',
      label: 'Date Acquired',
      type: 'date',
      required: true,
    },
    { name: 'remarks', label: 'Remarks', required: false },
  ];

  // Encode all property details into the QR code.
  const qrPayload = JSON.stringify({
    recordType: 'Property Inventory',
    ...item,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setItem((prev) => ({
      ...prev,
      [name]:
        name === 'unitValue' || name === 'totalAmount' ? Number(value) : value,
    }));

    // Prevent downloading a QR code containing outdated information.
    setGeneratedQR(null);
    setError('');
  };

  // Upload optional PNG logo.
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (file.type !== 'image/png') {
      setError('Please upload a PNG image.');
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Logo file must be 5 MB or smaller.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setLogoImage(reader.result);
        setGeneratedQR(null);
        setError('');
      }
    };

    reader.onerror = () => {
      setError('Unable to read the logo image.');
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoImage(null);
    setGeneratedQR(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Convert the QR canvas into a PNG and optionally add a logo.
  const generateQR = async (): Promise<string> => {
    const qrCanvas = qrCanvasRef.current;

    if (!qrCanvas) {
      throw new Error('QR canvas is not available.');
    }

    const qrSize = 800;
    const combinedCanvas = document.createElement('canvas');

    combinedCanvas.width = qrSize;
    combinedCanvas.height = qrSize;

    const ctx = combinedCanvas.getContext('2d');

    if (!ctx) {
      throw new Error('Unable to create canvas context.');
    }

    // White background for reliable QR scanning.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, qrSize, qrSize);
    ctx.drawImage(qrCanvas, 0, 0, qrSize, qrSize);

    if (logoImage) {
      const logo = new Image();

      await new Promise<void>((resolve, reject) => {
        logo.onload = () => resolve();
        logo.onerror = () => reject(new Error('Unable to load logo image.'));
        logo.src = logoImage;
      });

      // Keep the logo small to help maintain QR readability.
      const logoSize = Math.round(qrSize * 0.18);
      const logoX = (qrSize - logoSize) / 2;
      const logoY = (qrSize - logoSize) / 2;
      const padding = 12;

      // White backing behind the logo.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(
        logoX - padding,
        logoY - padding,
        logoSize + padding * 2,
        logoSize + padding * 2
      );

      ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);
    }

    return combinedCanvas.toDataURL('image/png');
  };

  const handleGenerate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsGenerating(true);
    setError('');
    setGeneratedQR(null);

    try {
      const image = await generateQR();
      setGeneratedQR(image);
    } catch (err) {
      console.error('QR generation failed:', err);
      setError('Failed to generate the QR code. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Download the QR code and property details as an A4 PDF.
  const handleDownloadPDF = () => {
    if (!generatedQR) {
      setError('Generate the QR code before downloading.');
      return;
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = 210;
    const centerX = pageWidth / 2;

    // Document heading.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('PROPERTY INVENTORY', centerX, 20, {
      align: 'center',
    });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Property QR Code Record', centerX, 27, {
      align: 'center',
    });

    // Property QR code.
    const qrSize = 82;
    const qrX = (pageWidth - qrSize) / 2;
    const qrY = 34;

    doc.addImage(generatedQR, 'PNG', qrX, qrY, qrSize, qrSize);

    // Property number and description.
    let y = qrY + qrSize + 9;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(item.propertyNumber || 'Property Number', centerX, y, {
      align: 'center',
    });

    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    const descriptionLines = doc.splitTextToSize(
      `Description: ${item.description}`,
      170
    );

    doc.text(descriptionLines, 20, y);
    y += descriptionLines.length * 5 + 5;

    // Property detail table-style layout.
    const details: [string, string][] = [
      ['Unit of Measure', item.unitOfMeasure],
      [
        'Unit Value',
        item.unitValue.toLocaleString('en-PH', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      ],
      [
        'Total Amount',
        item.totalAmount.toLocaleString('en-PH', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      ],
      ['ICS No.', item.icsNo],
      ['Date Acquired', item.dateAcquired],
      ['Remarks', item.remarks || 'None'],
    ];

    details.forEach(([label, value]) => {
      const valueLines = doc.splitTextToSize(value || '-', 115);

      const rowHeight = Math.max(9, valueLines.length * 5 + 4);

      // Start a new page if the details exceed the current page.
      if (y + rowHeight > 275) {
        doc.addPage();
        y = 20;
      }

      doc.setDrawColor(220, 224, 230);
      doc.line(20, y, 190, y);

      doc.setFont('helvetica', 'bold');
      doc.text(label, 22, y + 6);

      doc.setFont('helvetica', 'normal');
      doc.text(valueLines, 75, y + 6);

      y += rowHeight;
    });

    doc.setDrawColor(220, 224, 230);
    doc.line(20, y, 190, y);

    // Save PDF using the property number.
    const filename = (item.propertyNumber || 'property-inventory').replace(
      /[^a-z0-9_-]/gi,
      '_'
    );

    doc.save(`Property_${filename}.pdf`);
  };

  // Download only the QR image as PNG.
  const handleDownloadPNG = () => {
    if (!generatedQR) return;

    const filename = (item.propertyNumber || 'property').replace(
      /[^a-z0-9_-]/gi,
      '_'
    );

    const link = document.createElement('a');
    link.href = generatedQR;
    link.download = `QR_${filename}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <form
        onSubmit={handleGenerate}
        className="space-y-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
      >
        <div>
          <h1 className="text-xl font-bold text-slate-800">
            Property Inventory
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Enter the property details and generate a downloadable QR code.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
          {fields.map((field) => (
            <div
              key={field.name}
              className={`flex flex-col gap-1.5 ${
                field.name === 'remarks' ? 'md:col-span-2' : ''
              }`}
            >
              <label
                htmlFor={field.name}
                className="text-xs font-semibold tracking-wide text-slate-600"
              >
                {field.label}
              </label>

              {field.name === 'remarks' ? (
                <textarea
                  id={field.name}
                  name={field.name}
                  value={item.remarks}
                  onChange={handleChange}
                  placeholder="Add remarks (optional)"
                  rows={2}
                  className="w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-slate-500 focus:bg-white focus:ring-2 focus:ring-slate-100"
                />
              ) : (
                <input
                  id={field.name}
                  name={field.name}
                  type={field.type ?? 'text'}
                  value={item[field.name]}
                  onChange={handleChange}
                  placeholder={
                    field.type === 'number'
                      ? '0.00'
                      : field.name === 'description'
                      ? 'Enter description'
                      : ''
                  }
                  min={field.type === 'number' ? 0 : undefined}
                  step={field.type === 'number' ? '0.01' : undefined}
                  required={field.required}
                  className="h-10 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-slate-500 focus:bg-white focus:ring-2 focus:ring-slate-100"
                />
              )}
            </div>
          ))}
        </div>

        {/* Optional logo upload */}
        <div className="space-y-2 border-t border-slate-100 pt-4">
          <label
            htmlFor="logo"
            className="block text-sm font-semibold text-slate-700"
          >
            QR Code Logo (optional PNG)
          </label>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              ref={fileInputRef}
              id="logo"
              type="file"
              accept="image/png"
              onChange={handleLogoUpload}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-slate-200"
            />

            {logoImage && (
              <button
                type="button"
                onClick={handleRemoveLogo}
                className="rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-100"
              >
                Remove Logo
              </button>
            )}
          </div>

          {logoImage && (
            <div className="flex items-center gap-3">
              <img
                src={logoImage}
                alt="Uploaded logo preview"
                className="h-14 w-14 rounded border border-slate-200 object-contain p-1"
              />
              <p className="text-xs text-emerald-600">
                Logo uploaded successfully.
              </p>
            </div>
          )}

          <p className="text-xs text-slate-500">
            A small logo will be placed in the center of the QR code.
          </p>
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <div className="flex justify-end border-t border-slate-100 pt-4">
          <button
            type="submit"
            disabled={isGenerating}
            className="rounded-md bg-slate-800 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isGenerating ? 'Generating...' : 'Generate QR Code'}
          </button>
        </div>
      </form>

      {/* QR preview canvas; the generated image is exported separately. */}
      <div className="hidden" aria-hidden="true">
        <QRCodeCanvas
          ref={qrCanvasRef}
          value={qrPayload}
          size={800}
          level="H"
          marginSize={4}
          bgColor="#ffffff"
          fgColor="#0f172a"
        />
      </div>

      {/* Generated QR preview and download actions */}
      {generatedQR && (
        <section className="rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <h2 className="text-lg font-bold text-slate-800">
            QR Code Generated
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            {item.description} · {item.propertyNumber}
          </p>

          <div className="mx-auto my-5 w-fit rounded-lg border border-slate-100 bg-white p-3">
            <img
              src={generatedQR}
              alt={`Property QR code for ${item.propertyNumber}`}
              className="h-64 w-64 max-w-full object-contain"
            />
          </div>

          <p className="mb-5 text-xs text-slate-500">
            The QR code contains the property data in JSON format.
          </p>

          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={handleDownloadPNG}
              className="rounded-md bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700"
            >
              Download QR Code (PNG)
            </button>

            <button
              type="button"
              onClick={handleDownloadPDF}
              className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              Download Property PDF
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default PropertyInventoryForm;
